import { BehaviorSubject, Observable } from 'rxjs';

// TODO: Move interface to lib/_shared/types/
export interface IPlayer {
    playing$: Observable<boolean>;
    play(): void;
    pause(): void;
}

/**
 * Minimal cross-origin postMessage bridge to a 23video iframe player.
 * Implements only the surface used by `PlayerTwentyThree` (`bind`, `get`, `set`)
 * via the 23video JSON-over-postMessage protocol. Inlined from the upstream
 * `glueframe@1.1.2` CJS package to drop a non-ESM dependency from the Sirius
 * publish surface; see Sirius modernization PR for the full rationale.
 */
class GlueFrameBridge {
    private readonly _domain: string;
    private readonly _callbacks = new Map<string, (val: unknown) => void>();
    private readonly _glueFrameId = Date.now().toString();
    private _callbackCount = 0;

    constructor(
        private readonly _iframe: HTMLIFrameElement,
        private readonly _appName: string,
    ) {
        this._domain = String(_iframe.src).split('/').slice(0, 3).join('/');
        window.addEventListener('message', this._onMessage);
    }

    bind(event: string, callback: (val: unknown) => void): void {
        const cbId = this._registerCallback(callback);
        this._post({ f: 'bind', args: [event], cbId, triggerQueue: true });
    }

    get(prop: string, callback: (val: unknown) => void): void {
        const cbId = this._registerCallback(callback);
        this._post({ f: 'get', args: [prop], cbId });
    }

    set(prop: string, val: unknown): void {
        this._post({ f: 'set', args: [prop, val] });
    }

    destroy(): void {
        window.removeEventListener('message', this._onMessage);
        this._callbacks.clear();
    }

    private _registerCallback(callback: (val: unknown) => void): string {
        const id = `${this._glueFrameId}_${++this._callbackCount}`;
        this._callbacks.set(id, callback);
        return id;
    }

    private _post(message: Record<string, unknown>): void {
        this._iframe.contentWindow?.postMessage(JSON.stringify(message), this._domain);
    }

    private _onMessage = (event: MessageEvent): void => {
        if (event.origin !== this._domain) return;
        let data: { cbId?: string; a?: unknown } | undefined;
        try {
            data = JSON.parse(event.data);
        } catch {
            return;
        }
        if (data && typeof data.cbId === 'string') {
            const cb = this._callbacks.get(data.cbId);
            if (cb) cb(data.a);
        }
    };
}

export class PlayerDefault implements IPlayer {
    private _playing = new BehaviorSubject<boolean>(false);

    playing$ = this._playing.asObservable();

    constructor(private _video: HTMLMediaElement) {
        this._video.onplay = () => {
            this._playing.next(true);
        };

        this._video.onpause = () => {
            if (!this._video.seeking) this._playing.next(false);
        };
    }

    play(): void {
        this._video.play();
    }

    pause(): void {
        this._video.pause();
    }
}

export class PlayerTwentyThree implements IPlayer {
    private _player: GlueFrameBridge;
    private _playing = new BehaviorSubject<boolean>(false);

    playing$ = this._playing.asObservable();

    constructor(private _iframe: HTMLIFrameElement) {
        this._player = new GlueFrameBridge(this._iframe, 'Player');

        this._player.bind('player:video:play', () => {
            this._playing.next(true);
        });

        this._player.bind('player:video:pause', () => {
            this._player.get('seeking', (val) => {
                if (!val) this._playing.next(false);
            });
        });
    }

    play(): void {
        this._player.set('playing', true);
    }

    pause(): void {
        this._player.set('playing', false);
    }
}

/**
 * @deprecated Use `IPlayer` instead.
 */
export type Player = IPlayer;
